import { useQuery } from "@tanstack/react-query";
import { BoardStatusEnum } from "enums/BoardStatusEnum";
import {
  defaultSkip,
  defaultTake,
  formatUrlParams,
  getBoardStatusFromUrl,
  getOptionalStringFromUrl,
  getSkipFromUrl,
  getTakeFromUrl,
} from "helpers";
import { createLogger } from "logging";
import { BoardModel } from "models/BoardModel";
import { PaginatedList } from "models/PaginatedList";
import { useApi, usePermissions } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { BoardsQuery } from "services/BoardsApi";

import { BoardsContext, BoardsContextProps } from "./BoardsContext";

const logger = createLogger("BoardsProvider");
const QUERY_KEY = "boards-list";

const defaultStatus: BoardStatusEnum | undefined = undefined;

function getDefaultQuery() {
  return {
    skip: defaultSkip,
    take: defaultTake,
    status: defaultStatus,
  } as BoardsQuery;
}

function getUrlState(filters: BoardsQuery) {
  const o: Record<string, string | undefined> = {};
  if (filters.q) o.q = filters.q;
  if (filters.skip && filters.skip !== defaultSkip) o.skip = filters.skip.toString();
  if (filters.take && filters.take !== defaultTake) o.take = filters.take.toString();
  if (filters.status && filters.status !== defaultStatus) o.status = BoardStatusEnum[filters.status];

  const params = formatUrlParams(o);
  logger.debug(`getUrlState(): filters:=${JSON.stringify(filters)} -> '${params}'`);
  return [params, o];
}

function setUrlState(filters: BoardsQuery) {
  let [url, o] = getUrlState(filters);
  url = url ? `?${url}` : window.location.pathname;
  logger.debug("setUrlState(): newUrl:=", url);
  window.history.pushState(o, "", url);
}

export const BoardsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { boardsApi } = useApi();

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [query, setQuery] = useState<BoardsQuery>(() => ({
    q: getOptionalStringFromUrl(urlParams, "q"),
    skip: getSkipFromUrl(urlParams, defaultSkip),
    take: getTakeFromUrl(urlParams, defaultTake),
    status: getBoardStatusFromUrl(urlParams) ?? defaultStatus,
  }));

  useEffect(() => {
    setUrlState(query);
  }, [query]);

  useEffect(() => {
    const handlePopState = () => {
      logger.debug("Handling popstate event, updating state from URL");
      const params = new URLSearchParams(window.location.search);
      setQuery({
        q: getOptionalStringFromUrl(params, "q"),
        skip: getSkipFromUrl(params, defaultSkip),
        take: getTakeFromUrl(params, defaultTake),
        status: getBoardStatusFromUrl(params) ?? defaultStatus,
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const queryResult = useQuery<PaginatedList<BoardModel>>({
    queryKey: [QUERY_KEY, query],
    queryFn: async ({ signal }) => {
      const data = await boardsApi.getBoards(query, signal);
      logger.trace("Loaded", data);
      return data;
    },
  });
  const { error, refetch } = queryResult;

  const isLoading = queryResult.isFetching;
  const boards = queryResult.data;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const reload = useCallback(() => {
    refetch();
  }, [refetch]);

  const resetQuery = useCallback(() => {
    setQuery(getDefaultQuery());
  }, []);

  const contextValue: BoardsContextProps = useMemo(
    () => ({
      boards,
      isLoading,
      error,
      query,
      resetQuery,
      setQuery,
      reload,
    }),
    [boards, isLoading, error, query, setQuery, resetQuery, reload]
  );

  return <BoardsContext.Provider value={contextValue}>{children}</BoardsContext.Provider>;
};
